/**
 * @desc Helper functions for Prompt system, used to ensure prop correctness when creating a prompts
 * @external {@link https://github.com/bcgov/invasivesbc/wiki/User-Prompt-System }
 */
import { createAction } from '@reduxjs/toolkit';
import {
  ConfirmationModalInterface,
  DateModalInterface,
  ManualUtmModalInterface,
  NumberModalInterface,
  RadioModalInterface,
  TextModalInterface
} from 'interfaces/prompt-interfaces';
import { PromptTypes } from 'constants/promptEnums';

class Prompt {
  /**
   * @desc Action type for creating new prompts.
   */
  static readonly NEW_PROMPT = 'Prompt/NewPrompt';
  /**
   * @desc Helper function for creating modals to gather a 'boolean' input
   * @param {ConfirmationModalInterface} prompt component props
   */
  static readonly confirmation = createAction(this.NEW_PROMPT, (prompt: ConfirmationModalInterface) => ({
    payload: { ...prompt, type: PromptTypes.Confirmation }
  }));

  /**
   * @desc Helper function for creating modals to gather a 'Date' input
   * @param {DateModalInterface} prompt component props
   */
  static readonly date = createAction(this.NEW_PROMPT, (prompt: DateModalInterface) => ({
    payload: { ...prompt, type: PromptTypes.Date }
  }));

  /**
   * @desc Helper function for creating modals to gather a 'Number' input
   * @param {NumberModalInterface} prompt component props
   */
  static readonly number = createAction(this.NEW_PROMPT, (prompt: NumberModalInterface) => ({
    payload: { ...prompt, type: PromptTypes.Number }
  }));

  /**
   * @desc Helper function for creating modals to present options to a user
   * @param {RadioModalInterface} prompt component props
   */
  static readonly radio = createAction(this.NEW_PROMPT, (prompt: RadioModalInterface) => ({
    payload: { ...prompt, type: PromptTypes.Radio }
  }));

  /**
   * @desc Helper function for creating modals to gather a string input
   * @param {TextModalInterface} prompt component props
   */
  static readonly text = createAction(this.NEW_PROMPT, (prompt: TextModalInterface) => ({
    payload: { ...prompt, type: PromptTypes.Text }
  }));

  /**
   * @desc Helper function for creating modals to gather manual UTM inputs
   * @param {ManualUtmModalInterface} prompt component props
   */
  static readonly utm = createAction(this.NEW_PROMPT, (prompt: ManualUtmModalInterface) => ({
    payload: { ...prompt, type: PromptTypes.ManualUtm }
  }));

  /**
   * @desc DRY handler for closing input modal
   * @param id ID for the Payload object in the prompts array
   */
  static readonly closeOne = createAction('Prompt/closeOne', (id: string) => ({ payload: { id } }));

  /**
   * @desc Clear all input modals in the queue
   */
  static readonly closeAll = createAction('Prompt/closeAll');
}

export default Prompt;
